import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

const STORAGE_KEY = 'monquiz_dashboard_prefs';

const DEFAULT_PREFS = {
    selectedScopes: [],   // slugs des scopes choisis par l'utilisateur
    scopeOrder: [],       // slugs dans l'ordre personnalise par l'utilisateur
    lastScope: null,      // dernier scope consulte
    lastTab: {},          // { [scopeSlug]: 'quiz' | 'examen' | ... }
    lastSubTab: {},       // { [scopeSlug]: 'intelligent' | 'erreurs' | 'historique' | 'stats' }
    lastCategories: {},   // { [scopeSlug]: catId } — derniere categorie ouverte
    lastSousSujets: {},   // { [scopeSlug]: sousSujetId } — dernier sous-sujet interagi
    lastActiveThematique: null,  // slug de la derniere thematique perso active dans le hub
    customThematiqueOrder: [],    // slugs des thematiques perso dans l'ordre personnalise
    hiddenSharedScopes: [],       // slugs des thematiques partagees masquees du dashboard
    parcoursByScope: {},          // { [scopeSlug]: parcoursSlug } — '' = tout afficher
    landingScope: null,           // slug de la landing d'inscription — thématique d'arrivée à la 1re connexion
};

function loadFromStorage() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function saveToStorage(prefs) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch { /* quota exceeded */ }
}

function mergePrefs(base, overlay) {
    if (!overlay) return { ...base };
    const result = { ...base };
    for (const key of Object.keys(overlay)) {
        const bv = base[key];
        const ov = overlay[key];
        if (ov !== null && typeof ov === 'object' && !Array.isArray(ov) &&
            bv !== null && typeof bv === 'object' && !Array.isArray(bv)) {
            result[key] = { ...bv, ...ov };
        } else if (ov !== undefined) {
            result[key] = ov;
        }
    }
    return result;
}

export default function useDashboardPrefs(serverPrefs = null) {
    const [prefs, setPrefs] = useState(() => {
        const local = loadFromStorage();
        const merged = mergePrefs(DEFAULT_PREFS, mergePrefs(serverPrefs || {}, local || {}));
        if (!local && serverPrefs) saveToStorage(merged);
        return merged;
    });

    const syncTimeoutRef = useRef(null);

    // Hydrate from server prefs (login/reconnect)
    useEffect(() => {
        if (serverPrefs) {
            setPrefs(() => {
                const local = loadFromStorage();
                const next = mergePrefs(DEFAULT_PREFS, mergePrefs(serverPrefs, local || {}));
                saveToStorage(next);
                return next;
            });
        }
    }, [serverPrefs]);

    // Debounced sync to server (1s) — skip during impersonation to avoid overwriting target user's prefs
    const syncToServer = useCallback((newPrefs) => {
        if (localStorage.getItem('monquiz_impersonate')) return;
        // Visiteur non connecté : l'appel partirait quand même et reviendrait en 401,
        // que l'intercepteur traite comme une session expirée — il effacerait la
        // session et renverrait vers la connexion. Les préférences restent alors
        // dans le navigateur, ce qui est exactement ce qu'on veut pour lui.
        if (!localStorage.getItem('monquiz_token')) return;
        if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = setTimeout(() => {
            api.post('/user/preferences.php', newPrefs).catch(e => console.warn("Erreur:", e));
        }, 1000);
    }, []);

    useEffect(() => {
        return () => {
            if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
        };
    }, []);

    const updatePrefs = useCallback((partial) => {
        setPrefs((prev) => {
            const fresh = loadFromStorage() || prev;
            const next = mergePrefs(fresh, partial);
            saveToStorage(next);
            syncToServer(next);
            return next;
        });
    }, [syncToServer]);

    const setSelectedScopes = useCallback((scopes) => {
        updatePrefs({ selectedScopes: scopes });
    }, [updatePrefs]);

    const setScopeOrder = useCallback((order) => {
        updatePrefs({ scopeOrder: order });
    }, [updatePrefs]);

    const setLastScope = useCallback((slug) => {
        updatePrefs({ lastScope: slug });
    }, [updatePrefs]);

    const setTabForScope = useCallback((scopeSlug, tab) => {
        setPrefs((prev) => {
            const fresh = loadFromStorage() || prev;
            const next = { ...fresh, lastTab: { ...fresh.lastTab, [scopeSlug]: tab } };
            saveToStorage(next);
            syncToServer(next);
            return next;
        });
    }, [syncToServer]);

    // Parcours choisi dans une thématique qui en contient plusieurs. La chaîne
    // vide vaut « Tout afficher », et c'est le défaut : on ne cache jamais rien
    // tant que la personne n'a pas choisi.
    const setParcoursForScope = useCallback((scopeSlug, parcoursSlug) => {
        setPrefs((prev) => {
            const fresh = loadFromStorage() || prev;
            const next = { ...fresh, parcoursByScope: { ...fresh.parcoursByScope, [scopeSlug]: parcoursSlug || '' } };
            saveToStorage(next);
            syncToServer(next);
            return next;
        });
    }, [syncToServer]);

    const setCategoryForScope = useCallback((scopeSlug, catId) => {
        setPrefs((prev) => {
            const fresh = loadFromStorage() || prev;
            const next = { ...fresh, lastCategories: { ...fresh.lastCategories, [scopeSlug]: catId } };
            saveToStorage(next);
            syncToServer(next);
            return next;
        });
    }, [syncToServer]);

    const setSousSujetForScope = useCallback((scopeSlug, sousSujetId) => {
        setPrefs((prev) => {
            const fresh = loadFromStorage() || prev;
            const next = { ...fresh, lastSousSujets: { ...(fresh.lastSousSujets || {}), [scopeSlug]: sousSujetId } };
            saveToStorage(next);
            syncToServer(next);
            return next;
        });
    }, [syncToServer]);

    const setLastActiveThematique = useCallback((slug) => {
        updatePrefs({ lastActiveThematique: slug });
    }, [updatePrefs]);

    const setCustomThematiqueOrder = useCallback((order) => {
        updatePrefs({ customThematiqueOrder: order });
    }, [updatePrefs]);

    const setSubTabForScope = useCallback((scopeSlug, subTab) => {
        setPrefs((prev) => {
            const fresh = loadFromStorage() || prev;
            const next = { ...fresh, lastSubTab: { ...(fresh.lastSubTab || {}), [scopeSlug]: subTab } };
            saveToStorage(next);
            syncToServer(next);
            return next;
        });
    }, [syncToServer]);

    return {
        prefs,
        updatePrefs,
        setSelectedScopes,
        setScopeOrder,
        setLastScope,
        setTabForScope,
        setSubTabForScope,
        setCategoryForScope,
        setSousSujetForScope,
        setParcoursForScope,
        setLastActiveThematique,
        setCustomThematiqueOrder,
    };
}
